// Core
import { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import {
  Button,
  Box,
  Flex,
  Text,
  useColorModeValue,
  useColorMode
} from '@chakra-ui/react';

// Accets
import { apiService } from 'services/ApiService';
import { getInitials, validateLoadMore, parseDateTime } from 'services/Utils';
import { ReactComponent as EmptyListNotifications } from 'assets/svg/empty-list-notifications.svg';
import { ReactComponent as ReceiptIcon } from 'assets/svg/notifications/receipt.svg';
import { ReactComponent as AchievementsIcon } from 'assets/svg/notifications/achievements.svg';
import { ReactComponent as ChallengeIcon } from 'assets/svg/notifications/achievements.svg';
import { ReactComponent as GameIcon } from 'assets/svg/notifications/game.svg';
import { ReactComponent as MarketplaceIcon } from 'assets/svg/wallet/slider/marketplace.svg';
import { ReactComponent as CashbackIcon } from 'assets/svg/notifications/bonus.svg';
import { ReactComponent as CoinGiveawaysIcon } from 'assets/svg/notifications/coin-giveaways.svg';
import { ReactComponent as MysteryBoxIcon } from 'assets/svg/notifications/mystery-box.svg';
import { ReactComponent as ReviewIcon } from 'assets/svg/notifications/review.svg';
import { ReactComponent as PackageCoinsIcon } from 'assets/svg/notifications/package-coins.svg';
import { ReactComponent as PackageEntriesIcon } from 'assets/svg/notifications/package-entries.svg';
import { ReactComponent as LMCTCoinIcon } from 'assets/svg/lmctcoin.svg';
import { ReactComponent as SuperchargeIcon } from 'assets/svg/wallet/slider/supercharge.svg';

// Components
import Card from 'components/Card/Card';

const Transactions = ({ userData }) => {
  const border = useColorModeValue('1px solid #CBE7FF', '1px solid #262D47');
  const boxShadow = useColorModeValue('unset', '0px 1px 3px 0px rgba(0, 0, 0, 0.10),0px 1px 2px 0px rgba(0, 0, 0, 0.06)');

  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';

  const limit = 20;
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState([]);
  const [dataMeta, setDataMeta] = useState(); 
  const { isFetching } = useQuery({
      queryKey: ['coins-transactions', offset],
      queryFn: async () => {
        if(!localStorage.getItem('auth')) { return true; }

        const response = await apiService.getCashback({ limit, offset });
        if(offset === 0){
            setData(response.data);
        } else {
            setData([...data, ...response.data]);
        }
        setDataMeta(response.meta);
        return true;
      },
      refetchOnWindowFocus: false
  });

  const loadMore = async () => {
      setOffset(offset + limit);
  };

  return (
  	<>
    	{ data.length > 0 ? (
		    <Card padding="0px 0 32px 12px" borderRadius="12px" border={border} boxShadow={boxShadow}>	    
			    { data.map((item, index) => (
			        <Flex key={index} p="0" mt="12px" gap="20px" alignItems="center" w="100%">
			          <Box p="4px" w="32px" h="32px" borderRadius="8px" bg="rgba(9, 155, 255, 0.10)" position="relative">
			            { item?.type === 'receipt' && (<ReceiptIcon fontSize="24px" />)}
                  { item?.type === 'achievement' && (<AchievementsIcon fontSize="24px" />)}
                  { item?.type === 'game' && (<GameIcon fontSize="24px" />)}
                  { item?.type === 'cashback' && (<CashbackIcon fontSize="24px" />)}
                  { item?.type === 'review' && (<ReviewIcon fontSize="24px" />)}
                  { item?.type === 'mysteryBox' && (<MysteryBoxIcon fontSize="24px" />)}
                  { item?.type === 'challenge' && (<ChallengeIcon fontSize="24px" />)}
                  { item?.type === 'marketplace' && (<MarketplaceIcon fontSize="24px" />)}
                  { item?.type === 'packageCoins' && (<PackageCoinsIcon fontSize="24px" />)}
                  { item?.type === 'packageEntries' && (<PackageEntriesIcon fontSize="24px" />)}
                  { item?.type === 'giveaway' && (<CoinGiveawaysIcon fontSize="24px" />)}
                  { item?.type === 'partnerDiscount' && (<SuperchargeIcon fontSize="24px" />)}
			          </Box>

			          <Flex borderBottom={border} pb="10px" w="100%" justifyContent="space-between">
			          	<Box>
				            <Text 
				              fontSize="16px" 
				              fontWeight="600"
				              w="100%" 
				              textTransform="capitalize"
				              overflow="hidden" 
				              display="-webkit-box" 
				              style={{
				                WebkitLineClamp: 2, 
				                WebkitBoxOrient: 'vertical',
				                textOverflow: 'ellipsis'
				              }}
				            >
				              {item?.title}
				            </Text>
				            <Text variant="text-md" fontSize="15px" my="3px">
				            	{item?.description}
				            </Text>
				            <Text mt="3px" variant="text-xs" color={ isDark ? '#9FABCB' : '#262D47' }>{parseDateTime(item?.createdAt)}</Text>
			            </Box>

			            <Flex 
			            	gap="4px"
			            	alignItems="center"
			            	as={Text}
			            	variant="title-md"
			            	color={ item?.amount === 0 ? 
	          					'#FFB91B' : item?.amount > 0 ? 
	          					'#57D175' : '#FF6C64' } 
			            	pr="8px">{
			            		item?.amount > 0 ? 
			            		<><span>+</span><LMCTCoinIcon fontSize="12px" color="#57D175" /><span>{item?.amount}</span></>
			            		: item?.amount === 0 ? item?.amount 
			            		: 
			            		<><span>-</span><LMCTCoinIcon fontSize="12px" color="#FF6C64" /><span>{item?.amount * -1}</span></>
			            	}
			            	</Flex>
			          </Flex>


			        </Flex>
			    ))}

			    { validateLoadMore(data, dataMeta) && (
			        <Button 
			        	variant="ghost-md" 
			        	maxW="220px" 
			        	onClick={loadMore} 
			        	transform="translate(-50%, 16px)"
    						left="50%"
			        	isLoading={isFetching}>
			          Load More
			        </Button>
			    )}
		    </Card>
	    ) : (
	    	<Box 
              textAlign="center" 
              py="40px" 
              maxW={{sm: '325px'}} 
              left="50%" 
              position="relative" 
              transform="translate(-50%, 0px)">
                <Text variant="title-md" fontSize="20px" mt="20px" pb="9px" letterSpacing="-0.4px">Transaction list is empty</Text>
                <Text>We’ll let you know when there will be something to update you</Text>
            </Box>
	    )}
    </>
  );
};

export default Transactions;